import clsx from 'clsx';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { KeenIcon } from '@/components/keenicons';
import {
  IMenuItemConfig,
  Menu,
  MenuArrow,
  MenuBadge,
  MenuBullet,
  TMenuConfig,
  MenuHeading,
  MenuIcon,
  MenuItem,
  MenuLabel,
  MenuLink,
  MenuSub,
  MenuTitle,
} from '@/components/menu';
import { useFilteredMenu } from '@/hooks/useFilteredMenu';
import { useDemo1Layout } from '../Demo1LayoutProvider';

const PermissionBasedSidebarMenu = () => {
  const { filteredSidebar } = useFilteredMenu();
  const { layout } = useDemo1Layout();
  const { pathname } = useLocation();
  const prevPathnameRef = useRef<string>(pathname);
  const isSidebarCollapsed =
    (layout as any).options?.sidebar?.collapse || false; // eslint-disable-line @typescript-eslint/no-explicit-any

  // Move focus to main content when route changes
  useEffect(() => {
    // Only move focus if the pathname actually changed (not on initial mount)
    if (
      prevPathnameRef.current !== pathname &&
      prevPathnameRef.current !== ''
    ) {
      let timeoutId: NodeJS.Timeout | null = null;
      let rafId: number | null = null;
      let retryCount = 0;
      const maxRetries = 15;

      // Use requestAnimationFrame to ensure DOM is ready, then wait for content
      const moveFocus = (retry = false) => {
        const mainContent = document.getElementById('main-content');
        if (mainContent) {
          // Make the main content focusable if it isn't already
          if (!mainContent.hasAttribute('tabindex')) {
            mainContent.setAttribute('tabindex', '-1');
          }

          // Try to find the first focusable element within main content
          // Priority: buttons, links, then inputs
          const focusableSelectors = [
            'button:not([disabled]):not([tabindex="-1"])',
            'a[href]:not([tabindex="-1"])',
            'input:not([disabled]):not([tabindex="-1"])',
            'select:not([disabled]):not([tabindex="-1"])',
            'textarea:not([disabled]):not([tabindex="-1"])',
            '[tabindex]:not([tabindex="-1"])',
            '[contenteditable="true"]',
          ].join(', ');

          const firstFocusable = mainContent.querySelector(
            focusableSelectors
          ) as HTMLElement;

          if (firstFocusable) {
            firstFocusable.focus();
          } else if (retry && retryCount < maxRetries) {
            // Retry if no focusable element found and we haven't exceeded max retries
            retryCount++;
            timeoutId = setTimeout(() => moveFocus(true), 250);
          } else {
            // Fallback to focusing the main content itself
            mainContent.focus();
          }
        } else if (retry && retryCount < maxRetries) {
          // Retry if main content not found yet
          retryCount++;
          timeoutId = setTimeout(() => moveFocus(true), 250);
        }
      };

      // Use requestAnimationFrame to ensure DOM is ready
      rafId = requestAnimationFrame(() => {
        // Then use setTimeout to allow async content to load
        // Longer timeout for routes that might have async data loading or permission checks
        timeoutId = setTimeout(() => moveFocus(true), 400);
      });

      // Cleanup function
      return () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
        }
      };
    }
    prevPathnameRef.current = pathname;
  }, [pathname]);

  // Move focus to main content when a navigation link is clicked (for immediate feedback)
  const handleNavigationClick = useCallback(() => {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Then use setTimeout to allow React Router navigation to complete
      setTimeout(() => {
        const mainContent = document.getElementById('main-content');
        if (mainContent) {
          // Make the main content focusable if it isn't already
          if (!mainContent.hasAttribute('tabindex')) {
            mainContent.setAttribute('tabindex', '-1');
          }

          // Try to find the first focusable element within main content
          const focusableSelectors = [
            'a[href]:not([tabindex="-1"])',
            'button:not([disabled]):not([tabindex="-1"])',
            'input:not([disabled]):not([tabindex="-1"])',
            'select:not([disabled]):not([tabindex="-1"])',
            'textarea:not([disabled]):not([tabindex="-1"])',
            '[tabindex]:not([tabindex="-1"])',
            '[contenteditable="true"]',
          ].join(', ');

          const firstFocusable = mainContent.querySelector(
            focusableSelectors
          ) as HTMLElement;

          if (firstFocusable) {
            firstFocusable.focus();
          } else {
            // Fallback to focusing the main content itself
            mainContent.focus();
          }
        }
      }, 200);
    });
  }, []);

  const linkPl = 'ps-[10px]';
  const linkPr = 'pe-[10px]';
  const linkPy = 'py-[6px]';
  const itemsGap = 'gap-0.5';
  const subLinkPy = 'py-[8px]';
  const rightOffset = 'me-[-10px]';
  const iconWidth = 'w-[20px]';
  const iconSize = 'text-lg';
  const accordionLinkPl = 'ps-[10px]';
  const accordionLinkGap = [
    'gap-[10px]',
    'gap-[14px]',
    'gap-[5px]',
    'gap-[5px]',
    'gap-[5px]',
    'gap-[5px]',
  ];
  const accordionPl = [
    'ps-[10px]',
    'ps-[22px]',
    'ps-[22px]',
    'ps-[22px]',
    'ps-[22px]',
    'ps-[22px]',
  ];
  const accordionBorderLeft = [
    'before:start-[20px]',
    'before:start-[32px]',
    'before:start-[32px]',
    'before:start-[32px]',
    'before:start-[32px]',
  ];

  const buildMenu = (items: TMenuConfig) => {
    return items.map((item, index) => {
      if (item.heading) {
        return buildMenuHeading(item, index);
      } else if (item.disabled) {
        return buildMenuItemRootDisabled(item, index);
      } else {
        return buildMenuItemRoot(item, index);
      }
    });
  };

  const buildMenuItemRoot = (item: IMenuItemConfig, index: number) => {
    if (item.children) {
      return (
        <MenuItem
          key={index}
          {...(item.toggle && { toggle: item.toggle })}
          {...(item.trigger && { trigger: item.trigger })}
        >
          <MenuLink
            className={clsx(
              'flex items-center grow cursor-pointer border border-transparent',
              accordionLinkGap[0],
              linkPl,
              linkPr,
              linkPy
            )}
            {...(isSidebarCollapsed && { 'aria-label': item.title })}
          >
            <MenuIcon
              className={clsx(
                'items-start text-gray-500 dark:text-white/70 menu-item-active:text-primary dark:menu-item-active:!text-blue-400 menu-link-hover:!text-primary dark:menu-link-hover:!text-white contrast:text-white contrast:menu-item-active:!text-black',
                iconWidth
              )}
            >
              {item.icon && (
                <KeenIcon
                  icon={item.icon}
                  className={clsx(
                    iconSize,
                    'contrast:menu-item-active:!text-black'
                  )}
                />
              )}
            </MenuIcon>
            <MenuTitle className="text-sm font-medium text-gray-800 dark:text-white/70 menu-item-active:text-primary dark:menu-item-active:!text-blue-400 menu-link-hover:!text-primary dark:menu-link-hover:!text-white contrast:menu-link-hover:!text-white contrast:text-white contrast:menu-item-active:!text-black min-w-0 truncate">
              {item.title}
            </MenuTitle>
            {buildMenuArrow()}
          </MenuLink>
          <MenuSub
            className={clsx(
              'relative before:absolute before:top-0 before:bottom-0 before:border-s before:border-gray-200 dark:before:border-0 contrast:before:border-0',
              itemsGap,
              accordionBorderLeft[0],
              accordionPl[0]
            )}
          >
            {buildMenuItemChildren(item.children, index, 1)}
          </MenuSub>
        </MenuItem>
      );
    } else {
      return (
        <MenuItem key={index}>
          <MenuLink
            path={item.path}
            className={clsx(
              'border border-transparent menu-item-active:bg-secondary-active dark:menu-item-active:bg-coal-300 dark:menu-item-active:border-0 menu-item-active:rounded-lg hover:bg-secondary-active dark:hover:bg-coal-300 hover:rounded-lg contrast:menu-item-active:bg-white contrast:hover:bg-gray-100 contrast:menu-item-active:hover:bg-white contrast:menu-item-active:border-0',
              accordionLinkGap[0],
              linkPy,
              linkPl,
              linkPr
            )}
            handleClick={handleNavigationClick}
            {...(isSidebarCollapsed && { 'aria-label': item.title })}
          >
            <MenuIcon
              className={clsx(
                'items-start text-gray-600 dark:text-white/70 menu-item-active:text-primary dark:menu-item-active:!text-blue-400 menu-link-hover:!text-primary dark:menu-link-hover:!text-white contrast:text-white contrast:menu-item-active:!text-black contrast:menu-link-hover:!text-white contrast:menu-item-active:menu-link-hover:!text-black',
                iconWidth
              )}
            >
              {item.icon && (
                <KeenIcon
                  icon={item.icon}
                  className={clsx(
                    iconSize,
                    'contrast:menu-item-active:!text-black'
                  )}
                />
              )}
            </MenuIcon>
            <MenuTitle className="text-sm font-medium text-gray-800 dark:text-white/70 menu-item-active:text-primary dark:menu-item-active:!text-blue-400 menu-link-hover:!text-primary dark:menu-link-hover:!text-white contrast:text-white contrast:menu-item-active:!text-black contrast:menu-link-hover:!text-white contrast:menu-item-active:menu-link-hover:!text-black min-w-0 truncate">
              {item.title}
            </MenuTitle>
          </MenuLink>
        </MenuItem>
      );
    }
  };

  const buildMenuItemRootDisabled = (item: IMenuItemConfig, index: number) => {
    return (
      <MenuItem key={index}>
        <MenuLabel
          className={clsx(
            'border border-transparent',
            accordionLinkGap[0],
            linkPy,
            linkPl,
            linkPr
          )}
        >
          <MenuIcon
            className={clsx(
              'items-start text-gray-500 dark:text-gray-600 contrast:text-white',
              iconWidth
            )}
          >
            {item.icon && (
              <KeenIcon
                icon={item.icon}
                className={clsx(iconSize, 'contrast:text-white')}
              />
            )}
          </MenuIcon>
          <MenuTitle className="text-sm font-medium text-gray-800 contrast:text-white">
            {item.title}
          </MenuTitle>

          {item.disabled && buildMenuSoon()}
        </MenuLabel>
      </MenuItem>
    );
  };

  const buildMenuItemChildren = (
    items: TMenuConfig,
    index: number,
    level: number = 0
  ) => {
    return items.map((item, index) => {
      if (item.disabled) {
        return buildMenuItemChildDisabled(item, index, level);
      } else {
        return buildMenuItemChild(item, index, level);
      }
    });
  };

  const buildMenuItemChild = (
    item: IMenuItemConfig,
    index: number,
    level: number = 0
  ) => {
    if (item.children) {
      return (
        <MenuItem
          key={index}
          {...(item.toggle && { toggle: item.toggle })}
          {...(item.trigger && { trigger: item.trigger })}
          className={clsx(item.collapse && 'flex-col-reverse')}
        >
          <MenuLink
            className={clsx(
              'border border-transparent grow cursor-pointer',
              accordionLinkGap[level],
              accordionLinkPl,
              linkPr,
              subLinkPy
            )}
            {...(isSidebarCollapsed && {
              'aria-label': item.collapse
                ? item.expandTitle || item.title
                : item.title,
            })}
          >
            {buildMenuBullet()}

            {item.collapse ? (
              <MenuTitle className="text-2sm font-normal text-gray-600 dark:text-white/70 contrast:text-white">
                <span className="hidden menu-item-show:!flex">
                  {item.collapseTitle}
                </span>
                <span className="flex menu-item-show:hidden">
                  {item.expandTitle}
                </span>
              </MenuTitle>
            ) : (
              <MenuTitle className="text-2sm font-normal me-1 text-gray-800 dark:text-white/70 menu-item-active:text-primary dark:menu-item-active:!text-blue-400 menu-item-active:font-medium menu-link-hover:!text-primary dark:menu-link-hover:!text-white contrast:text-white contrast:menu-item-active:!text-black contrast:menu-link-hover:!text-white contrast:menu-item-active:menu-link-hover:!text-black min-w-0 truncate">
                {item.title}
              </MenuTitle>
            )}

            {buildMenuArrow()}
          </MenuLink>
          <MenuSub
            className={clsx(
              !item.collapse &&
                'relative before:absolute before:top-0 before:bottom-0 before:border-s before:border-gray-200 dark:before:border-0 contrast:before:border-0',
              itemsGap,
              !item.collapse && accordionBorderLeft[level],
              !item.collapse && accordionPl[level],
              !item.collapse && 'relative before:absolute'
            )}
          >
            {buildMenuItemChildren(
              item.children,
              index,
              item.collapse ? level : level + 1
            )}
          </MenuSub>
        </MenuItem>
      );
    } else {
      return (
        <MenuItem key={index}>
          <MenuLink
            path={item.path}
            className={clsx(
              'border border-transparent items-center grow menu-item-active:bg-secondary-active dark:menu-item-active:bg-coal-300 dark:menu-item-active:border-0 menu-item-active:rounded-lg hover:bg-secondary-active dark:hover:bg-coal-300 hover:rounded-lg contrast:menu-item-active:bg-white contrast:hover:bg-gray-100 contrast:menu-item-active:hover:bg-white contrast:menu-item-active:border-0',
              accordionLinkGap[level],
              accordionLinkPl,
              linkPr,
              subLinkPy
            )}
            handleClick={handleNavigationClick}
            {...(isSidebarCollapsed && { 'aria-label': item.title })}
          >
            {buildMenuBullet()}
            <MenuTitle className="text-2sm font-normal text-gray-800 dark:text-white/70 menu-item-active:text-primary dark:menu-item-active:!text-blue-400 menu-item-active:font-semibold menu-link-hover:!text-primary dark:menu-link-hover:!text-white contrast:text-white contrast:menu-item-active:!text-black contrast:menu-link-hover:!text-white contrast:menu-item-active:menu-link-hover:!text-black min-w-0 truncate">
              {item.title}
            </MenuTitle>
          </MenuLink>
        </MenuItem>
      );
    }
  };

  const buildMenuItemChildDisabled = (
    item: IMenuItemConfig,
    index: number,
    level: number = 0
  ) => {
    return (
      <MenuItem key={index}>
        <MenuLabel
          className={clsx(
            'border border-transparent items-center grow',
            accordionLinkGap[level],
            accordionLinkPl,
            linkPr,
            subLinkPy
          )}
        >
          {buildMenuBullet()}
          <MenuTitle className="text-2sm font-normal text-gray-800 contrast:text-white">
            {item.title}
          </MenuTitle>
          {item.disabled && buildMenuSoon()}
        </MenuLabel>
      </MenuItem>
    );
  };

  const buildMenuHeading = (item: IMenuItemConfig, index: number) => {
    return (
      <MenuItem key={index} className="pt-2.25 pb-px">
        <MenuHeading
          className={clsx(
            'uppercase text-2sm font-medium text-gray-500 dark:text-white/50 contrast:text-white',
            linkPl,
            linkPr
          )}
        >
          {item.heading}
        </MenuHeading>
      </MenuItem>
    );
  };

  const buildMenuArrow = () => {
    return (
      <MenuArrow
        className={clsx(
          'text-gray-400 dark:text-white/60 w-[20px] shrink-0 justify-end ms-1 contrast:text-white',
          rightOffset
        )}
      >
        <KeenIcon icon="plus" className="text-2xs menu-item-show:hidden" />
        <KeenIcon
          icon="minus"
          className="text-2xs hidden menu-item-show:inline-flex"
        />
      </MenuArrow>
    );
  };

  const buildMenuBullet = () => {
    return (
      <MenuBullet className="flex w-[6px] relative before:absolute before:top-0 before:size-[6px] before:rounded-full rtl:before:translate-x-1/2 before:-translate-y-1/2 menu-item-active:before:bg-primary dark:menu-item-active:before:!bg-blue-400 menu-item-hover:before:bg-primary dark:menu-item-hover:before:!bg-white dark:before:bg-white/40 contrast:menu-item-active:before:bg-black contrast:menu-item-hover:before:bg-white"></MenuBullet>
    );
  };

  const buildMenuSoon = () => {
    return (
      <MenuBadge className={rightOffset}>
        <span className="badge badge-xs">Soon</span>
      </MenuBadge>
    );
  };

  return (
    <Menu
      highlight={true}
      multipleExpand={false}
      className={clsx('flex flex-col grow', itemsGap)}
    >
      {filteredSidebar && buildMenu(filteredSidebar)}
    </Menu>
  );
};

export { PermissionBasedSidebarMenu };
